import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Layout } from '../components/Layout';
import { useMyFamilies } from '../hooks/useMyFamilies';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AlertCircle, AlertTriangle, ArrowLeft, Calendar, CheckCircle, Info, MapPin, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { Spinner } from '../components/ui/spinner.tsx';
import { useCheckIn } from '../hooks/useEvents';
import { AttendeeType } from '../types/enums';
import { formatAtlanticDate, formatAtlanticTime } from '@/utils/time.utils.ts';
import { getStatusColor, getStatusLabel } from '@/utils/event-attendee-status.utils.ts';
import { eventService } from '../api/eventService';
import { familyService } from '../api/familyService';

interface LocationState {
  event: {
    id: string;
    name: string;
    startDateTime: string;
    endDateTime: string;
    location?: string;
    dropIn: boolean;
  };
  selectedMembers: string[];
  memberDetails: Array<{
    id: string;
    firstName: string;
    lastName: string;
    type: AttendeeType;
    age?: number;
    ageGroup?: string;
  }>;
}

import { MemberWithStatus } from '@/types/family.types';

export const CheckInConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  const { user, families } = useMyFamilies();
  const queryClient = useQueryClient();

  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [membersWithStatus, setMembersWithStatus] = useState<MemberWithStatus[]>([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const checkInMutation = useCheckIn();
  const fetchStatusAbortControllerRef = useRef<AbortController | null>(null);
  const { t } = useTranslation('common');

  // Fetch full family details to get primaryAdult phone for emergency contact
  const primaryFamilyId = families[0]?.id;
  const { data: familyDetails } = useQuery({
    queryKey: ['familyDetails', primaryFamilyId],
    queryFn: () => familyService.getMyFamilyDetails(primaryFamilyId!),
    enabled: !!primaryFamilyId,
    staleTime: Infinity,
  });

  const primaryAdult = familyDetails?.primaryAdult;
  const emergencyContactName = primaryAdult
    ? `${primaryAdult.firstName} ${primaryAdult.lastName}`
    : user
      ? `${user.firstName} ${user.lastName}`
      : undefined;
  const emergencyContactPhone = primaryAdult?.phone || undefined;

  // Fetch registration status for each selected member (only for non-drop-in events)
  useEffect(() => {
    // Cancel any pending requests
    if (fetchStatusAbortControllerRef.current) {
      fetchStatusAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    fetchStatusAbortControllerRef.current = abortController;

    const fetchRegistrationStatuses = async () => {
      if (state.event.dropIn || !state.selectedMembers.length) {
        // For drop-in events, no need to fetch status
        if (!abortController.signal.aborted) {
          setMembersWithStatus(
            state.memberDetails.map((m) => ({
              ...m,
              registrationStatus: null,
            })),
          );
        }
        return;
      }

      setIsLoadingStatus(true);
      try {
        // Use batch endpoint to get all statuses in one request
        const statuses = await eventService.getBatchRegistrationStatuses(state.event.id, state.selectedMembers);

        // Check if request was aborted
        if (abortController.signal.aborted) return;

        // Map statuses back to members
        const statusMap = new Map(statuses.map((s) => [s.attendeeId, s.registrationStatus]));

        const membersWithStatuses = state.memberDetails
          .filter((m) => state.selectedMembers.includes(m.id))
          .map((member) => ({
            ...member,
            registrationStatus: statusMap.get(member.id) || null,
          }));

        if (!abortController.signal.aborted) {
          setMembersWithStatus(membersWithStatuses);
        }
      } catch (error) {
        console.error('Error fetching registration statuses:', error);
        // Fallback to no status
        if (!abortController.signal.aborted) {
          setMembersWithStatus(
            state.memberDetails.map((m) => ({
              ...m,
              registrationStatus: null,
            })),
          );
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingStatus(false);
        }
      }
    };

    fetchRegistrationStatuses();

    // Cleanup function to abort pending requests
    return () => {
      abortController.abort();
    };
  }, [state.event.id, state.event.dropIn, state.selectedMembers, state.memberDetails]);

  // Registration mutation - similar structure to backoffice bulk registration
  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!user || !families.length) {
        throw new Error('User or family information not available');
      }

      const primaryFamily = families[0];
      const registeredByName = `${user.firstName} ${user.lastName}`;

      // Prepare registrations array - similar to backoffice BulkRegistrationRequest
      const registrations = state.selectedMembers.map((memberId) => {
        const member = state.memberDetails.find((m) => m.id === memberId);
        if (!member) throw new Error(`Member ${memberId} not found`);

        return {
          attendeeId: memberId,
          attendeeType: member.type,
          registeredByPersonId: primaryFamily.id,
          attendeeName: `${member.firstName} ${member.lastName}`,
          registeredByName,
          emergencyContactName,
          emergencyContactPhone,
          specialNeedsNotes: undefined,
          dietaryRestrictions: undefined,
          needsTransportation: false,
          needsMeals: false,
          needsChildcare: false,
        };
      });

      // Use bulk registration method - similar to backoffice
      return await eventService.registerMultipleAttendees(state.event.id, registrations);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['member-events'] });
      // Clear upcoming events cache to force reload
      localStorage.removeItem('upcomingEvents');
      queryClient.invalidateQueries({ queryKey: ['upcomingEvents'] });

      // Show success message based on results - similar to backoffice
      if (result.failedCount === 0) {
        toast.success(
          `Successfully registered ${result.succeeded} ${result.succeeded === 1 ? 'attendee' : 'attendees'}!`,
        );
      } else {
        toast.error(
          `Registered ${result.succeeded} ${result.succeeded === 1 ? 'attendee' : 'attendees'}. ${result.failedCount} ${result.failedCount === 1 ? 'failed' : 'failed'}.`,
        );
      }

      // Refetch statuses using batch endpoint
      const fetchStatuses = async () => {
        try {
          const statuses = await eventService.getBatchRegistrationStatuses(state.event.id, state.selectedMembers);

          const statusMap = new Map(statuses.map((s) => [s.attendeeId, s.registrationStatus]));

          const membersWithStatuses = state.memberDetails
            .filter((m) => state.selectedMembers.includes(m.id))
            .map((member) => ({
              ...member,
              registrationStatus: statusMap.get(member.id) || null,
            }));

          setMembersWithStatus(membersWithStatuses);
        } catch (error) {
          console.error('Error refetching registration statuses:', error);
          // Fallback to no status
          setMembersWithStatus(
            state.memberDetails.map((m) => ({
              ...m,
              registrationStatus: null,
            })),
          );
        }
      };
      fetchStatuses();
    },
    onError: (error: unknown) => {
      const err = error as {
        response?: { data?: { error?: { message?: string }; message?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to register attendees';
      toast.error(errorMessage);
    },
  });

  const handleConfirmCheckIn = () => {
    if (!user) {
      toast.error('User information not available');
      return;
    }

    const checkedInByName = `${user.firstName} ${user.lastName}`;

    const attendees = state.selectedMembers.map((id) => {
      const member = state.memberDetails.find((m) => m.id === id);
      return {
        id,
        name: member ? `${member.firstName} ${member.lastName}` : undefined,
        type: member?.type || AttendeeType.ADULT,
      };
    });

    checkInMutation.mutate(
      {
        eventId: state.event.id,
        attendees,
        checkedInByName,
        emergencyContactName,
        emergencyContactPhone,
      },
      {
        onSuccess: (result) => {
          if (result.succeeded > 0 && result.failed === 0) {
            setIsCheckedIn(true);

            // Invalidate caches
            queryClient.invalidateQueries({ queryKey: ['upcomingEvents'] });
            queryClient.invalidateQueries({ queryKey: ['pastEvents'] });
            localStorage.removeItem('upcomingEvents');
            localStorage.removeItem('pastEvents');

            const attendeesWithDetails = state.selectedMembers.map((id) => ({
              id,
              ...state.memberDetails.find((member) => member.id === id),
            }));

            const resultData = {
              eventName: state.event.name,
              eventDate: state.event.startDateTime,
              eventId: state.event.id,
              attendees: attendeesWithDetails.map((attendee) => ({
                attendeeId: attendee.id,
                attendeeName: `${attendee.firstName} ${attendee.lastName}`,
                checkedInAt: new Date().toISOString(),
                registrationStatus: 'attended',
              })),
              checkedInByName,
              totalCheckedIn: result.succeeded,
            };

            setTimeout(() => {
              navigate('/check-in-result', { state: resultData });
            }, 1000);
          }
        },
        onError: (error: unknown) => {
          // Error is already handled by the hook's onError (toast notification)
          // Additional error handling can be added here if needed
          console.error('Check-in error:', error);
        },
      },
    );
  };

  // Determine action based on registration statuses
  const actionState = useMemo(() => {
    if (state.event.dropIn) {
      return {
        type: 'check-in' as const,
        canSubmit: true,
        buttonText: t('events.confirmCheckIn'),
        message: null,
      };
    }

    const statuses = membersWithStatus
      .filter((m) => state.selectedMembers.includes(m.id))
      .map((m) => m.registrationStatus?.toLowerCase() || 'none');

    const hasConfirmed = statuses.some((s) => s === 'confirmed');
    const allConfirmed = statuses.every((s) => s === 'confirmed');
    const allRegistered = statuses.every((s) => s === 'registered') && !hasConfirmed;
    const allNone = statuses.every((s) => s === 'none');
    const hasMixed = new Set(statuses.filter((s) => s !== 'none')).size > 1;

    if (allNone) {
      return {
        type: 'register' as const,
        canSubmit: true,
        buttonText: t('events.requestRegistration'),
        message: null,
      };
    }

    if (allConfirmed) {
      return {
        type: 'check-in' as const,
        canSubmit: true,
        buttonText: t('events.confirmCheckIn'),
        message: null,
      };
    }

    if (allRegistered) {
      return {
        type: 'disabled' as const,
        canSubmit: false,
        buttonText: t('events.confirmCheckIn'),
        message: 'All selected members are awaiting confirmation. Please wait for confirmation before checking in.',
      };
    }

    if (hasMixed) {
      return {
        type: 'disabled' as const,
        canSubmit: false,
        buttonText: t('events.confirmCheckIn'),
        message: 'Selected members have different registration statuses. Please select members with the same status.',
      };
    }

    // Default: can't proceed
    return {
      type: 'disabled' as const,
      canSubmit: false,
      buttonText: t('events.confirmCheckIn'),
      message: 'Some members are not registered or confirmed. Please register first.',
    };
  }, [state.event.dropIn, membersWithStatus, state.selectedMembers, t]);

  const groupMembersByType = () => {
    const adults = membersWithStatus.filter((m) => m.type === AttendeeType.ADULT);
    const children = membersWithStatus.filter((m) => m.type === AttendeeType.CHILD);
    return { adults, children };
  };

  const handleSubmit = () => {
    if (actionState.type === 'register') {
      registerMutation.mutate();
    } else if (actionState.type === 'check-in') {
      handleConfirmCheckIn();
    }
  };

  if (!state) {
    return (
      <Layout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>No event or members selected. Please go back and select an event.</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/events')} className="mt-4">
          {t('events.backToEvents')}
        </Button>
      </Layout>
    );
  }

  const { adults, children } = groupMembersByType();

  return (
    <Layout>
      <div className="space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          disabled={checkInMutation.isPending || isCheckedIn}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('events.backToEvents')}
        </Button>

        <Card className="card-gradient">
          {state.event.dropIn && (
            <CardHeader>
              <CardTitle className="text-2xl text-(--color-neutral-900)">{t('events.checkInConfirmation')}</CardTitle>
            </CardHeader>
          )}
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2 text-(--color-neutral-900)">{state.event.name}</h3>
              <div className="space-y-2 text-sm text-(--color-neutral-700) text-center">
                <div>
                  <div className="flex items-center justify-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>{formatAtlanticDate(state.event.startDateTime)}</span>
                  </div>
                  {state.event.startDateTime && (
                    <div className="mt-1 text-sm text-(--color-neutral-700)">
                      <p>
                        {t('events.start')}: {formatAtlanticTime(state.event.startDateTime)}
                      </p>
                      <p>
                        {t('events.end')}: {formatAtlanticTime(state.event.endDateTime)}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  <span>{state.event.location || t('events.noLocation')}</span>
                </div>
                <div className="mt-2">
                  {state.event.dropIn ? (
                    <Badge className="badge-event">{t('events.dropInEvent')}</Badge>
                  ) : (
                    <Badge variant="warning">{t('events.preRegistrationRequired')}</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {state.selectedMembers.length < state.memberDetails.length && (
          <Alert className="bg-(--color-secondary-100) border-(--color-secondary-300)">
            <Info className="h-4 w-4 text-(--color-secondary-500)" />
            <AlertDescription className="text-(--color-secondary-700)">
              Some family members cannot attend this event due to age restrictions and have been automatically excluded
              from check-in.
            </AlertDescription>
          </Alert>
        )}

        {!state.event.dropIn && actionState.message && (
          <Alert className="bg-(--color-warning-100) border-(--color-warning-300)">
            <AlertTriangle className="h-4 w-4 text-(--color-warning-700)" />
            <AlertDescription className="text-(--color-warning-700)">{actionState.message}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('events.attendees.selected')}</span>
              <Badge variant="secondary">
                <Users className="h-3 w-3 mr-1" />
                {state.selectedMembers.length} {t('events.attendees.total')}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {adults.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-(--color-neutral-700) mb-2">
                  {t('events.attendees.adults')} ({adults.length})
                </h4>
                <div className="space-y-2">
                  {adults.map((adult) => (
                    <div key={adult.id} className="flex flex-col gap-2 p-3 bg-neutral-300/20 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <CheckCircle className="h-4 w-4 text-(--color-success-500)" />
                          <span className="font-medium">
                            {adult.firstName} {adult.lastName}
                          </span>
                        </div>
                        <Badge variant="outline">{t('events.attendees.adultBadge')}</Badge>
                      </div>
                      {!state.event.dropIn && adult.registrationStatus && (
                        <div className="flex items-center gap-2 ml-7">
                          <Badge
                            variant={null}
                            className={`whitespace-nowrap flex-shrink-0 ${getStatusColor(adult.registrationStatus)}`}
                          >
                            {t(getStatusLabel(adult.registrationStatus))}
                          </Badge>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {children.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-(--color-neutral-700) mb-2">
                  {t('events.attendees.children')} ({children.length})
                </h4>
                <div className="space-y-2">
                  {children.map((child) => (
                    <div key={child.id} className="flex flex-col gap-2 p-3 bg-neutral-300/20 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <CheckCircle className="h-4 w-4 text-(--color-success-500)" />
                          <span className="font-medium">
                            {child.firstName} {child.lastName}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {child.age && (
                            <Badge variant="outline">{t('events.attendees.ageBadge', { age: child.age })}</Badge>
                          )}
                          {child.ageGroup && <Badge variant="secondary">{child.ageGroup}</Badge>}
                        </div>
                      </div>
                      {!state.event.dropIn && child.registrationStatus && (
                        <div className="flex items-center gap-2 ml-7">
                          <Badge
                            variant={null}
                            className={`whitespace-nowrap flex-shrink-0 ${getStatusColor(child.registrationStatus)}`}
                          >
                            {t(getStatusLabel(child.registrationStatus))}
                          </Badge>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={checkInMutation.isPending || registerMutation.isPending || isCheckedIn || isLoadingStatus}
            className="flex-1"
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={
              !actionState.canSubmit ||
              checkInMutation.isPending ||
              registerMutation.isPending ||
              isCheckedIn ||
              isLoadingStatus
            }
            className={`flex-1 ${
              isCheckedIn ? 'bg-(--color-success-500) hover:bg-(--color-success-500)' : 'btn-event'
            }`}
          >
            {isLoadingStatus ? (
              <>
                <Spinner className="h-4 w-4 mr-2" />
                Loading...
              </>
            ) : registerMutation.isPending ? (
              <>
                <Spinner className="h-4 w-4 mr-2" />
                Registering...
              </>
            ) : checkInMutation.isPending ? (
              <>
                <Spinner className="h-4 w-4 mr-2" />
                Checking In...
              </>
            ) : isCheckedIn ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Checked In Successfully
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                {actionState.buttonText}
              </>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
};
